import React, { useState, useEffect } from 'react';
import { ButtonStyles, ButtonContainer, ArrowIcon } from './NavigationButton.styles';
import { handleNext } from '@helper/navigationHelpers';
import { ModalSubmit } from '../../Modals/ModalSubmit/ModalSubmit';
import { pages } from '../../../defaultVerbiages';
import { useAppSelector, useAppDispatch } from '@app/hooks';
import _ from 'lodash';
import { ConfirmationModal } from '@/components/Modals/ConfirmModal/ConfirmModal';
import {
  decrementStep,
  startLoading,
  stopLoading,
  selectIsLoading,
  selectErrorResponsePersisted,
  selectIsFirstTimeGetStarted,
  selectIsFirstTimeGetStartedAddress,
  setIsFirstTimeGetStartedAddress,
  setIsFirstTimeVMO,
  selectIsFirstTimeVMO,
  selectIsFirstTimeEmploymentInformation,
  setIsFirstTimeEmploymentInformation,
  setSkippedVMO,
  selectHasRepriced,
  setHasRepriced,
} from '@/reducers/currentPage';
import { formatOccupancyDate } from '@/utils/handleDate';
import {
  handleGetStarted,
  handleGetStartedAddress,
  executeAction,
  handleRequestVmo,
  handleRequestEmploymentDetails,
  handleRequestPersonalInformation,
  handleGovernmentInformation,
  handleRequestDeclarations,
} from '@/utils/handleEndpoints';
import { DefaultModal } from '@Modals/DefaultModal/DefaultModal';
import { TextBlock } from '@/components/text/TextBlock';
import CodeInput from '@/components/input/CodeInput/CodeInput';
import { TypeOfText } from '@/components/text/TextBlock';
import { useIsMobile } from '@/hooks/useIsMobile';
import CodeInputV1 from '@/components/input/CodeInputV1/CodeInputV1';
import { handleProgramSelection } from '@/utils/handleVmo';

export enum PageOrigin {
  GET_STARTED = 'get-started',
  GET_STARTED_ADDRESS = 'get-started-address',
  ESTIMATED_AVAILABLE_EQUITY = 'estimated-available-equity',
  VMO = 'vmo',
  VMO_LINE_AMOUNT = 'vmo-line-amount',
  VMO_DRAW_AMOUNT = 'vmo-draw-amount',
  PERSONAL_INFORMATION = 'personal-information',
  EMPLOYMENT_INFORMATION = 'employment-information',
  GOVERNMENT_MONITORING_INFORMATION = 'government-monitoring-information',
  DECLARATIONS = 'declarations',
  INQUIRY_SUMMARY = 'inquiry-summary',
  COMPLETION = 'completion',
}
export interface NavigationButtonProps {
  nextButton: string;
  isBackButton: boolean;
  backButton: string;
  variant: 'primary' | 'secondary' | 'tertiary';
  type?: string;
  disabled: boolean;
  buttonSize?: 's' | 'm' | 'l';
  icon?: any;
  navigation?: (page: any) => void;
  onAccess: (page: string) => void;
  pageOrigin?: PageOrigin;
  destinationPage?: PageOrigin;
  currentPage?: PageOrigin;
  hasIcon?: boolean;
  isCustomColor?: boolean;
  customColor?: string;
  customColorBorder?: string;
  customColorText?: string;
  isSummaryPage?: boolean;
  onNext?: () => void;
  hasDataChanged?: () => boolean;
  resetChangeDetection?: () => void;
  infoModal?: {
    header?: string;
    body?: string;
    label?: string;
    button?: string;
  };
  onBackClick?: () => void;
}

export interface CurrentFormProps {
  firstName: string;
  middleName: string;
  lastName: string;
}

export const NavigationButton: React.FC<NavigationButtonProps> = ({
  nextButton,
  variant,
  buttonSize = 's',
  isBackButton,
  backButton,
  type,
  disabled = false,
  onAccess,
  navigation,
  pageOrigin,
  currentPage,
  destinationPage,
  hasIcon = false,
  isCustomColor,
  customColor,
  customColorBorder,
  customColorText,
  isSummaryPage = false,
  onNext,
  hasDataChanged,
  resetChangeDetection,
  onBackClick,
}) => {
  const [openModal, setOpenModal] = useState(false);
  let loanApplication = useAppSelector((state) => state.LoanApplication);
  let templateConfig = useAppSelector((state) => state.TemplateConfig);
  let currentForm = useAppSelector((state) => state.CurrentForm);
  const isFirstPage = !hasIcon;
  const isGetStarted = currentPage === 'get-started';
  const dispatch = useAppDispatch();
  const isICE = templateConfig?.isICE ?? false;
  const isLoanDepot = templateConfig?.lenderName === 'Loan Depot';
  const isLoading = useAppSelector(selectIsLoading);
  const [errorResponse, setErrorResponse] = useState<string | unknown>('');
  const errorResponsePersisted = useAppSelector(selectErrorResponsePersisted);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  let auxLoanApplication = _.cloneDeep(loanApplication);
  const isMobile = useIsMobile(780);
  const isResumeV2 = (templateConfig?.features?.resume?.version === 2 && templateConfig?.features?.resume?.isEnabled) ?? true;
  const isResumeV1 = (templateConfig?.features?.resume?.isEnabled && templateConfig?.features?.resume?.version !== 2) ?? false;
  const [isCodeInput, setIsCodeInput] = useState(false);
  const [openResume, setOpenResume] = useState(false);
  const isFirstTimeGetStarted = useAppSelector(selectIsFirstTimeGetStarted);
  const isFirstTimeGetStartedAddress = useAppSelector(selectIsFirstTimeGetStartedAddress);
  const isFirstTimeVMO = useAppSelector(selectIsFirstTimeVMO);
  const isFirstTimeEmploymentInformation = useAppSelector(selectIsFirstTimeEmploymentInformation);
  const isVmoV2 = loanApplication?.features?.vmoRedesign || false;
  const hasRepriced = useAppSelector(selectHasRepriced);

  useEffect(() => {
    if (errorResponsePersisted) {
      setErrorResponse(errorResponsePersisted);
      setOpenErrorModal(true);
    }
  }, [errorResponsePersisted]);

  useEffect(() => {
    if (errorResponse) {
      setErrorResponse(errorResponse);
      setOpenErrorModal(true);
    }
  }, [errorResponse, setErrorResponse, dispatch]);

  const closeErrorModal = () => {
    setOpenErrorModal(false);
  };

  useEffect(() => {
    if (currentPage === 'get-started') {
      auxLoanApplication.borrowers[0].borrowerEmailAddress = currentForm.email;
    }
    dispatch({
      type: 'LoanApplication/updateValue',
      payload: { ...auxLoanApplication },
    });
  }, [currentForm.email]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClick = async (action: string, shouldSkipVMO = false) => {
    if (action === 'next' && onNext) {
      onNext();
    }
    if (type === 'submit' && action !== 'back') {
      if (currentPage === 'get-started') {
        const occupancyDateFormat = currentForm?.dataOccupancyDate ? formatOccupancyDate(currentForm.dataOccupancyDate) : '';
        const updatedBorrower = {
          ...auxLoanApplication.borrowers[0],
          borrowerLoanPurpose: currentForm.purposeOfLoan,
          borrowerFirstName: currentForm.firstName,
          borrowerMiddleName: currentForm.middleName,
          borrowerLastName: currentForm.lastName,
          ...(currentForm.suffix && { borrowerSuffixName: currentForm.suffix }),
          borrowerEmailAddress: currentForm.email,
          borrowerPrimaryPhone: currentForm.mobilePhone || '',
          borrowerAlternatePhone: currentForm.homePhone || '',
          borrowerWorkPhone: currentForm.workPhone || '',
        };
        const updateConsent = {
          ...auxLoanApplication.consent,
          softPullConsent: currentForm?.softPullConsent ?? false,
          eConsent: currentForm.eConsent,
        };
        const updateProperty = {
          ...auxLoanApplication.borrowerProperty,
          propertyStreetAddress2: currentForm.suit,
          propertyOccupancyDate: occupancyDateFormat,
          propertyStreetAddress: currentForm.address || auxLoanApplication.borrowerProperty?.propertyStreetAddress || '',
          propertyCity: currentForm.city || auxLoanApplication.borrowerProperty?.propertyCity || '',
          propertyState: currentForm.state || auxLoanApplication.borrowerProperty?.propertyState || '',
          propertyZip: currentForm.zip || auxLoanApplication.borrowerProperty?.propertyZip || '',
          propertyCounty: currentForm.county || auxLoanApplication.borrowerProperty?.propertyCounty || '',
        };
        auxLoanApplication.propertyPrimaryUse = currentForm.primaryUse;
        auxLoanApplication.propertyType = currentForm.propertyType;
        auxLoanApplication.borrowers[0] = updatedBorrower;
        auxLoanApplication.borrowerProperty = updateProperty;
        auxLoanApplication.consent = updateConsent;
        setOpenModal(true);
      }

      dispatch({
        type: 'LoanApplication/updateValue',
        payload: { ...auxLoanApplication },
      });
      setOpenModal(true);
    } else {
      let page = action === 'next' ? destinationPage : pageOrigin;

      if (action === 'next' && currentPage === PageOrigin.ESTIMATED_AVAILABLE_EQUITY) {
        if (shouldSkipVMO) {
          page = PageOrigin.VMO_LINE_AMOUNT;
        }
      }

      if (action !== 'next') {
        dispatch(decrementStep());
      }
      if (!navigation) return;
      setOpenModal(false);

      if (currentPage === PageOrigin.GET_STARTED) {
        const auxLoanApplication = _.cloneDeep(loanApplication);
        auxLoanApplication.borrowerProperty = {
          ...auxLoanApplication.borrowerProperty,
          propertyZip: currentForm.zip || '',
        };
        dispatch({
          type: 'LoanApplication/updateValue',
          payload: { ...auxLoanApplication },
        });
      }

      if (!isLoading && !openErrorModal) handleNext(navigation, onAccess, currentPage!, `/${page}`);
    }
  };

  const processClick = async () => {
    let auxLoanApplication = { ...loanApplication };
    let shouldSkipVMO = false;
    dispatch(startLoading());

    try {
      if (currentPage === PageOrigin.DECLARATIONS) {
        await handleRequestDeclarations(isICE, auxLoanApplication, currentForm, dispatch, templateConfig);
      }

      if (currentPage === PageOrigin.GET_STARTED) {
        // check if any data has changed and if soft pull is enabled
        const dataHasChanged = hasDataChanged ? hasDataChanged() : false;
        const isSoftCreditPullEnabled = templateConfig?.features?.softCredit?.isEnabled ?? false;

        // Scenario 1: Return visit + No changes + No soft pull → Direct navigation
        if (!dataHasChanged && !isFirstTimeGetStarted && !isSoftCreditPullEnabled) {
          dispatch(stopLoading());
          if (!navigation) return;
          handleNext(navigation, onAccess, currentPage!, '/get-started-address');
          return;
        }
        // Scenario 2: Return visit + (Data changed OR Soft pull enabled) → Show ModalSubmit
        else if (!isFirstTimeGetStarted && (dataHasChanged || isSoftCreditPullEnabled) && isResumeV1) {
          await handleGetStarted(isLoanDepot, auxLoanApplication, dispatch);
          // resetChangeDetection will be called by ModalSubmit
        }
        // Scenario 3: First time visit → Show ModalSubmit
        else if (isFirstTimeGetStarted && isResumeV1) {
          await handleGetStarted(isLoanDepot, auxLoanApplication, dispatch);
          // don't set isFirstTimeGetStarted to false here - it should be set when user actually navigates
          // don't reset change detection here - ModalSubmit needs to detect changes
        }

        if (isResumeV1 && !isResumeV2) {
          const response = await handleGetStarted(isLoanDepot, auxLoanApplication, dispatch);
          if (response && response.data.isEligible) {
            setOpenModal(false);
            setIsCodeInput(true);
            setOpenResume(true);
            dispatch(stopLoading());
            return;
          }
        }
      }

      if (currentPage === PageOrigin.GET_STARTED_ADDRESS) {
        if (isFirstTimeGetStartedAddress || !hasDataChanged || hasDataChanged()) {
          await handleGetStartedAddress(isICE, auxLoanApplication, loanApplication, dispatch, templateConfig);
          if (isFirstTimeGetStartedAddress) {
            dispatch(setIsFirstTimeGetStartedAddress(false));
          }
          resetChangeDetection && resetChangeDetection();
        }
      }

      if (currentPage === PageOrigin.ESTIMATED_AVAILABLE_EQUITY) {
        if (auxLoanApplication.estimatedHomeEquity < 50000) {
          auxLoanApplication.lineAmount = 35000;
          auxLoanApplication.minLineAmount = 35000;
          auxLoanApplication.drawAmount = 35000;
          auxLoanApplication.fixedLineAmount = 35000;
        } else {
          auxLoanApplication.lineAmount = 50000;
          auxLoanApplication.minLineAmount = 50000;
          auxLoanApplication.minDrawAmount = 37500;
          auxLoanApplication.maxDrawAmount = 50000;
        }

        const response = await executeAction(isICE, auxLoanApplication, dispatch, templateConfig, false);

        let hasSingleProgram = false;
        let singleProgramData = null;

        if (response && 'data' in response && response.data) {
          if (isICE) {
            const { helRates, helocRates, isDisqualified } = response.data;

            const availableTypes = [];
            if (isDisqualified) {
              setErrorResponse('You are not eligible for this program.');
              setOpenErrorModal(true);
              return;
            }

            if (helRates && Array.isArray(helRates) && helRates.length > 0) {
              availableTypes.push({ programType: 'helRates', programs: helRates });
            }
            if (helocRates && Array.isArray(helocRates) && helocRates.length > 0) {
              availableTypes.push({ programType: 'helocRates', programs: helocRates });
            }

            if (availableTypes.length === 1 && availableTypes[0].programs.length === 1) {
              hasSingleProgram = true;
              singleProgramData = {
                programType: availableTypes[0].programType,
                program: availableTypes[0].programs[0],
              };
            }
          } else {
            const availablePrograms = response.data;

            if (availablePrograms && Object.keys(availablePrograms).length > 0) {
              const programTypes = Object.keys(availablePrograms);

              if (programTypes.length === 1) {
                const programType = programTypes[0];
                const programData = availablePrograms[programType];

                if (programData?.rates && Array.isArray(programData.rates) && programData.rates.length === 1) {
                  hasSingleProgram = true;
                  singleProgramData = { programType, program: programData.rates[0] };
                }
              }
            }
          }
        }

        if (hasSingleProgram && singleProgramData) {
          const { programType, program } = singleProgramData;

          auxLoanApplication.selectedProgramType = programType === 'helRates' || programType === 'helData' ? 'Home Equity Loan' : 'Line of Credit';

          const finalLoanApplication = handleProgramSelection(program, auxLoanApplication);

          dispatch({
            type: 'LoanApplication/updateValue',
            payload: { ...finalLoanApplication },
          });

          await handleRequestVmo(isICE, finalLoanApplication, dispatch, templateConfig);

          dispatch(setSkippedVMO(true));
          shouldSkipVMO = true;
        }
      }

      if (currentPage === PageOrigin.VMO) {
        const hasSelectedProgram = auxLoanApplication.selectedProgram || auxLoanApplication.selectedProgramName;

        if (isICE && hasSelectedProgram) {
          await executeAction(isICE, auxLoanApplication, dispatch, templateConfig, true);
        }

        await handleRequestVmo(isICE, auxLoanApplication, dispatch, templateConfig);

        if (isVmoV2 && isFirstTimeVMO) {
          dispatch(setIsFirstTimeVMO(false));
        }
        resetChangeDetection && resetChangeDetection();
      }

      if (currentPage === PageOrigin.VMO_LINE_AMOUNT) {
        if (isFirstTimeVMO || hasRepriced) {
          await handleRequestVmo(isICE, auxLoanApplication, dispatch, templateConfig);
          if (isFirstTimeVMO) {
            dispatch(setIsFirstTimeVMO(false));
          }
          resetChangeDetection && resetChangeDetection();
        }
        dispatch(setHasRepriced(false));
      }

      if (currentPage === PageOrigin.EMPLOYMENT_INFORMATION) {
        if (isFirstTimeEmploymentInformation || !hasDataChanged || hasDataChanged()) {
          await handleRequestEmploymentDetails(isICE, auxLoanApplication, dispatch, templateConfig);
          if (isFirstTimeEmploymentInformation) {
            dispatch(setIsFirstTimeEmploymentInformation(false));
          }
          resetChangeDetection && resetChangeDetection();
        }
      }

      if (currentPage === PageOrigin.PERSONAL_INFORMATION) {
        if (!hasDataChanged || hasDataChanged()) {
          await handleRequestPersonalInformation(isICE, auxLoanApplication, dispatch, templateConfig);
          resetChangeDetection?.();
        }
      }

      if (currentPage === PageOrigin.GOVERNMENT_MONITORING_INFORMATION) {
        await handleGovernmentInformation(isICE, auxLoanApplication, dispatch, templateConfig);
      }
    } catch (error) {
      console.error(`Error on ${currentPage} request:`, error);
      setErrorResponse(error);
      setOpenErrorModal(true);
      return undefined as unknown as boolean;
    } finally {
      dispatch(stopLoading());
    }

    return shouldSkipVMO;
  };

  return (
    <>
      <ButtonContainer $variant={variant} $buttonSize={buttonSize} $isGetStarted={isGetStarted} $isMobile={isMobile} $isBackButtonDisabled={isBackButton}>
        {!isBackButton && (
          <ButtonStyles
            $variant={'tertiary'}
            onClick={() => {
              if (onBackClick) {
                onBackClick();
              } else {
                handleClick('back');
              }
            }}
            $buttonSize={buttonSize}
            $isCustomColor={isCustomColor}
            $customColor={customColor}
            $customColorBorder={customColorBorder}
            $customColorText={customColorText}
          >
            {backButton}
            {!isFirstPage && <ArrowIcon />}
          </ButtonStyles>
        )}
        {!isMobile && isSummaryPage && (
          <>
            <TextBlock
              text={templateConfig?.pages?.summary?.programInformationLink?.textOne?.text}
              link={templateConfig?.pages?.summary?.programInformationLink?.textOne?.link}
              direction={'center'}
              color="textPrimary"
              type={TypeOfText.H4}
              isHidden={templateConfig?.pages?.summary?.programInformationLink?.textOne?.isHidden ?? false}
            />
            <TextBlock
              text={templateConfig?.pages?.summary?.programInformationLink?.textTwo?.text}
              link={templateConfig?.pages?.summary?.programInformationLink?.textTwo?.link}
              direction={'center'}
              color="textPrimary"
              type={TypeOfText.H4}
              isHidden={templateConfig?.pages?.summary?.programInformationLink?.textTwo?.isHidden ?? false}
            />
          </>
        )}
        <ButtonStyles
          disabled={disabled}
          $variant={variant}
          onClick={async () => {
            const shouldSkipVMO = await processClick();
            if (typeof shouldSkipVMO !== 'boolean') return;
            handleClick('next', shouldSkipVMO);
          }}
          $buttonSize={buttonSize}
          $isCustomColor={isCustomColor}
          $customColor={customColor}
          $customColorBorder={customColorBorder}
          $customColorText={customColorText}
        >
          {nextButton}
          {!isFirstPage && <ArrowIcon />}
        </ButtonStyles>
        {isMobile && isSummaryPage && (
          <>
            <TextBlock
              text={templateConfig?.pages?.summary?.programInformationLink?.textOne?.text}
              link={templateConfig?.pages?.summary?.programInformationLink?.textOne?.link}
              direction={'center'}
              color="textPrimary"
              type={TypeOfText.H4}
              isHidden={templateConfig?.pages?.summary?.programInformationLink?.textOne?.isHidden ?? false}
            />
            <TextBlock
              text={templateConfig?.pages?.summary?.programInformationLink?.textTwo?.text}
              link={templateConfig?.pages?.summary?.programInformationLink?.textTwo?.link}
              direction={'center'}
              color="textPrimary"
              type={TypeOfText.H4}
              isHidden={templateConfig?.pages?.summary?.programInformationLink?.textTwo?.isHidden ?? false}
            />
          </>
        )}
      </ButtonContainer>
      {isLoading && (
        <ModalSubmit
          openModal={openModal}
          setOpenModal={setOpenModal}
          infoSubmitModal={templateConfig?.pages?.LetsGetStarted?.modals?.infoSubmitModal || pages?.LetsGetStarted?.modals?.infoSubmitModal}
          onAccess={onAccess}
          hasDataChanged={hasDataChanged}
          resetChangeDetection={resetChangeDetection}
        />
      )}
      {openModal && !isSummaryPage && !openResume ? (
        <ModalSubmit
          openModal={openModal}
          setOpenModal={setOpenModal}
          infoSubmitModal={templateConfig?.pages?.LetsGetStarted?.modals?.infoSubmitModal || pages?.LetsGetStarted?.modals?.infoSubmitModal}
          onAccess={onAccess}
          hasDataChanged={hasDataChanged}
          resetChangeDetection={resetChangeDetection}
        />
      ) : isCodeInput ? (
        isResumeV1 ? (
          <CodeInputV1 isResume={openResume} setIsResume={setOpenResume} loanApplication={loanApplication} onAccess={onAccess} navigation={navigation} />
        ) : (
          <CodeInput isResume={openResume} setIsResume={setOpenResume} loanApplication={loanApplication} onAccess={onAccess} />
        )
      ) : (
        <ConfirmationModal
          openModal={openModal}
          setOpenModal={setOpenModal}
          infoModal={templateConfig?.pages?.summary?.modalSubmit?.infoSubmitModal || pages?.summary?.modalSubmit?.infoSubmitModal}
          isSummaryPage={isSummaryPage}
          onAccess={onAccess}
          navigation={navigation}
        />
      )}
      {openErrorModal && errorResponse && (
        <DefaultModal
          openModal={openErrorModal}
          setOpenModal={closeErrorModal}
          isErrorModal={true}
          infoModal={{
            header: (errorResponse as any)?.response?.error?.appActionTitle || '',
            body: typeof errorResponse === 'string' ? errorResponse : String(errorResponse || 'An error occurred'),
            button: (errorResponse as any)?.response?.error?.appActionButtonTitle || 'Return to %%lenderName%%',
          }}
          preventBackgroundInteraction={true}
        />
      )}
    </>
  );
};
